import React, { PropsWithChildren } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import { Box, Card, CircularProgress, Divider, IconButton, ListItem, Tooltip, capitalize } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ListHeader, ToolbarContainer, ViewNotesModal, dataModelIcons } from '/src/components';
import { useUserStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { Note } from '/src/lib/models';
import { DataModel, DataModelType, ListHeaderItem, dataModelPluralTypes } from '/src/lib/types';

export type ListPageProps<T extends DataModel> = PropsWithChildren & {
  /**
   * Data model to list
   */
  modelType: DataModelType;
  /**
   * Items to display in the list header
   */
  headerItems: ListHeaderItem[];
  /**
   * Data to display
   */
  data?: T[];
  /**
   * Sets the verbiage when talking about creating
   */
  createVerb?: string;
  /**
   * URL for this data model's Create page
   */
  createUrl?: string;
  /**
   * Element ID
   */
  id?: string;
  /**
   * Items to display in the list header's toolbar
   */
  toolbarItems?: React.ReactNode;
  /**
   * Field to sort by
   */
  sortType: string;
  /**
   * Sorts the list in reverse order
   */
  sortReverse: boolean;
  /**
   * Sets the field to sort by
   * @param sortType Field to sort by
   * @returns void
   */
  setSortType?: (sortType: string) => void;
  /**
   * Reverses the list direction
   * @param sortReverse Whether to sort in reverse
   * @returns void
   */
  setSortReverse?: (sortReverse: boolean) => void;
  /**
   * Sorts the data based on the given sortType
   * @param sortType Field to sort by
   * @returns void
   */
  sortList: (sortType: string) => void;
  /**
   * List of Notes to display
   */
  notes?: Note[];
  /**
   * Sets the list of Notes
   * @param notes Notes to display
   * @returns void
   */
  setNotes?: (notes?: Note[]) => void;
  /**
   * Title to display on View Notes modal
   */
  notesName?: string;
  /**
   * Sets the View Notes modal title
   * @param notes Notes to display
   * @returns void
   */
  setNotesName?: (name?: string) => void;
  /**
   * Show a link to the data model's Create page when list is empty
   */
  showCreateLink?: boolean;
  /**
   * Message to display if list is empty
   */
  noDataMessage?: string;
  /**
   * Flag indicating the list is being filtered
   */
  isFiltered?: boolean;
  /**
   * Message to display if the filtered list is empty
   */
  noFilteredDataMessage?: string;
  /**
   * Hides the page's Create button
   */
  hideCreateButton?: boolean;
  /**
   * Message to display in the List box
   */
  message?: string;
};

/**
 * List Page Layout
 */
export const ListPage = observer(
  <T extends DataModel>({
    children,
    data,
    // items,
    modelType,
    headerItems,
    createVerb = 'Add',
    createUrl,
    id,
    toolbarItems,
    // defaultSortType,
    // defaultSortReverse,
    sortType,
    sortReverse,
    sortList,
    notes,
    notesName,
    setNotes,
    setNotesName,
    showCreateLink = false,
    noDataMessage,
    isFiltered,
    noFilteredDataMessage,
    hideCreateButton,
    message,
  }: ListPageProps<T>) => {
    const rowHeight = 48;

    /** Context **/
    const userStore = useUserStore();
    const navigate = useNavigate();

    /** State **/
    // const [sortType, setSortType] = useState((defaultSortType || '') as string);
    // const [sortReverse, setSortReverse] = useState(defaultSortReverse || false);

    /** Computed **/
    const pageId = id || `list${modelType}`;
    const pageTitle = `${createVerb} ${modelType}`;
    const dataModelsName = capitalize(dataModelPluralTypes[modelType]);

    /** Methods **/

    /** Render **/
    return (
      <DesktopLayout>
        <Box id={pageId} component="md-content" className="md-default-theme">
          {/* TITLE / SEARCH / ACTIONS */}
          <Card component="md-card" className="md-default-theme">
            {/* PAGE HEADER */}
            <ToolbarContainer>
              <Box justifyContent="start" alignItems="center" display="flex" flex={33}>
                {toolbarItems}
              </Box>

              <Box justifyContent="center" alignItems="center" display="flex" flex={33}>
                {dataModelIcons[modelType]}
                <Box component="span" className="md-title pad-left-xs">
                  {dataModelsName}
                </Box>
              </Box>

              <Box justifyContent="end" alignItems="center" display="flex" flex={33}>
                {userStore.isAdmin && !hideCreateButton && (
                  <IconButton
                    className="md-button md-fab md-mini"
                    aria-label={pageTitle}
                    onClick={() => navigate(createUrl || `/${dataModelsName.toLowerCase()}/create`)}
                  >
                    <Tooltip title={pageTitle}>
                      <Add className="md-icon material-icons" />
                    </Tooltip>
                  </IconButton>
                )}
              </Box>
            </ToolbarContainer>

            {!data && (
              <Box
                bgcolor="white"
                display="flex"
                flex="100"
                justifyContent="center"
                alignItems="center"
                className="pad-top-bottom"
              >
                <CircularProgress className="md-primary" md-mode="indeterminate" />
              </Box>
            )}

            {data && (
              <Box bgcolor="white">
                {/* LIST */}
                {!!data.length && (
                  <Box bgcolor="white">
                    <ListHeader
                      items={headerItems}
                      sortType={sortType}
                      sortReverse={sortReverse}
                      sortList={sortList}
                      padForScrollbar={data.length > 11}
                    />
                    <Divider />
                    <Box
                      // component="md-content"
                      className={data.length > 11 ? 'fixed-list-lg' : ''}
                      sx={{ maxHeight: `${(11 * rowHeight).toString()}px` }}
                    >
                      {children}
                    </Box>
                  </Box>
                )}

                {message && (
                  <ListItem>
                    <Box
                      display="flex"
                      flex="100"
                      justifyContent="center"
                      alignItems="center"
                      className="pad-top-bottom"
                    >
                      {message}
                    </Box>
                  </ListItem>
                )}

                {!message && data && !data.length && isFiltered && (
                  <ListItem>
                    <Box
                      display="flex"
                      flex="100"
                      justifyContent="center"
                      alignItems="center"
                      className="pad-top-bottom md-title thin"
                    >
                      {noFilteredDataMessage || `No ${dataModelsName} found.`}
                    </Box>
                  </ListItem>
                )}

                {!message && data && !data.length && !isFiltered && (
                  <ListItem>
                    <Box
                      display="flex"
                      flex="100"
                      justifyContent="center"
                      alignItems="center"
                      className="pad-top-bottom md-title thin"
                    >
                      {showCreateLink && !userStore.isLender ? (
                        <Box>
                          No {dataModelsName} yet, why don't you <Box paddingRight="4px" />{' '}
                          <Link to={`/${dataModelsName.toLowerCase()}/create`}>create one</Link>?
                        </Box>
                      ) : (
                        noDataMessage || `No ${dataModelsName} found.`
                      )}
                    </Box>
                  </ListItem>
                )}

                {!message && !data && (
                  <ListItem>
                    <Box
                      display="flex"
                      flex="100"
                      justifyContent="center"
                      alignItems="center"
                      className="pad-top-bottom"
                    >
                      <CircularProgress className="md-primary" md-mode="indeterminate" />
                    </Box>
                  </ListItem>
                )}
              </Box>
            )}
          </Card>

          {notes && notesName && setNotes && setNotesName && (
            <ViewNotesModal
              notes={notes}
              name={notesName}
              open={!!notes}
              setOpen={(open) => !open && setNotes(undefined) && setNotesName(undefined)}
            />
          )}
        </Box>
      </DesktopLayout>
    );
  }
);
